import { type RefObject, useEffect, useRef } from 'react';

import { trapDialogFocus } from './focus';

function isBackdropClick(event: MouseEvent, dialog: HTMLDialogElement): boolean {
  if (event.target !== dialog) return false;
  const bounds = dialog.getBoundingClientRect();
  return (
    event.clientX < bounds.left ||
    event.clientX > bounds.right ||
    event.clientY < bounds.top ||
    event.clientY > bounds.bottom
  );
}

/** Owns native dialog lifecycle and backdrop dismissal without replacing focus handling. */
export function useDialog(open: boolean, onClose: () => void): RefObject<HTMLDialogElement | null> {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      dialog.scrollTop = 0;
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const dismiss = (event: MouseEvent): void => {
      if (isBackdropClick(event, dialog)) onClose();
    };
    const keepFocus = (event: KeyboardEvent): void => trapDialogFocus(event, dialog);
    dialog.addEventListener('click', dismiss);
    dialog.addEventListener('keydown', keepFocus);
    return () => {
      dialog.removeEventListener('click', dismiss);
      dialog.removeEventListener('keydown', keepFocus);
    };
  }, [onClose]);
  return ref;
}
